import React, { ReactNode } from 'react';
import { newRelicAgent } from './newrelic';
import { logger } from '../logger';

interface ErrorDetails {
  error: Error | string;
  componentName?: string;
  additionalData?: Record<string, any>;
}

interface ErrorBoundaryProps {
  componentName: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export const trackError = ({ error, componentName, additionalData }: ErrorDetails) => {
  const errorMessage = error instanceof Error ? error.message : error;
  const errorStack = error instanceof Error ? error.stack : undefined;

  // Send to New Relic
  newRelicAgent.noticeError(error, {
    componentName,
    errorMessage,
    errorStack,
    ...additionalData
  });

  // Log the error
  logger.error('error:tracked', {
    componentName,
    errorMessage,
    errorStack,
    additionalData
  });
};

// Global error boundary for React components
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    trackError({
      error,
      componentName: this.props.componentName,
      additionalData: {
        reactErrorInfo: errorInfo,
        componentStack: errorInfo.componentStack
      }
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <div>Something went wrong. Please try again.</div>;
    }

    return this.props.children;
  }
}
